import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import Loader from '@/components/Loader'
import Pointer from '@/components/Pointer'
import useTerminal, { PrinterNotReadyError } from '@/lib/useTerminal'

import useCheckout from '../../hooks/useCheckout'
import Step from '../Step/Step'
import { PaymentPendingContent, PaymentPendingPointer } from './PaymentPending.styles'

const Payment = () => {
  const { t } = useTranslation()
  const { orderId, setCurrentStep, setPaymentError, setPrinterStatus } = useCheckout()
  const [terminalMessage, setTerminalMessage] = useState(t('terminalCodes.default'))
  const [status, setStatus] = useState<'PAYMENT' | 'ORDER'>('PAYMENT')
  const orderIdRef = useRef<string | null>(null)
  const paymentFinalizedRef = useRef(false)
  // Tracks the orderId we've already passed to startPayment in this lifecycle so a StrictMode
  // double-effect or parent remount can't trigger a second terminal charge.
  const startPaymentInvokedForOrderIdRef = useRef<string | null>(null)
  const { startPayment, printOrder } = useTerminal()
  const handlePaymentStatusUpdate = useCallback(
    (statusPayload: unknown) => {
      let payload = statusPayload
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload) as unknown
        } catch {
          /* keep as-is */
        }
      }
      const parsedStatus =
        payload && typeof payload === 'object' && !Array.isArray(payload)
          ? (payload as { phase?: unknown; message?: unknown; isFinal?: unknown })
          : null

      const phase = typeof parsedStatus?.phase === 'string' ? parsedStatus.phase : ''
      const message = typeof parsedStatus?.message === 'string' ? parsedStatus.message : ''
      const isFinal = typeof parsedStatus?.isFinal === 'boolean' ? parsedStatus.isFinal : false

      if (message) {
        setTerminalMessage(message)
      }

      if (!isFinal || paymentFinalizedRef.current) {
        return
      }

      paymentFinalizedRef.current = true

      if (phase === 'success') {
        setStatus('ORDER')
        if (!orderIdRef.current) {
          setPaymentError(t('terminalError'))
          setCurrentStep('CONFIRMATION')
          return
        }

        void printOrder({ orderId: orderIdRef.current })
          .catch(error => {
            console.error('[printOrder] error', error)
          })
          .finally(() => {
            setCurrentStep('CONFIRMATION')
          })

        return
      }

      setPaymentError(message || t('terminalError'))
      setCurrentStep('CONFIRMATION')
    },
    [printOrder, setCurrentStep, setPaymentError, t]
  )

  useEffect(() => {
    const previousHandler = window.onPaymentStatusUpdate
    window.onPaymentStatusUpdate = handlePaymentStatusUpdate

    return () => {
      if (window.onPaymentStatusUpdate === handlePaymentStatusUpdate) {
        window.onPaymentStatusUpdate = previousHandler
      }
    }
  }, [handlePaymentStatusUpdate])

  useEffect(() => {
    setPaymentError(undefined)

    if (process.env.REACT_APP_SKIP_TERMINAL === 'true') {
      setStatus('ORDER')
      setCurrentStep('CONFIRMATION')
      return
    }

    if (!orderId) {
      setCurrentStep('PAYMENT_METHOD')
      return
    }

    const orderIdString = String(orderId)

    if (startPaymentInvokedForOrderIdRef.current === orderIdString) {
      console.warn(`[PaymentPending] startPayment already invoked for orderId=${orderIdString}, skipping`)
      return
    }

    let active = true
    paymentFinalizedRef.current = false
    setTerminalMessage(t('terminalCodes.default'))
    setStatus('PAYMENT')
    orderIdRef.current = orderIdString
    startPaymentInvokedForOrderIdRef.current = orderIdString

    void startPayment({ orderId: orderIdString }).catch(error => {
      if (!active) return
      // The money-safety gate refused the charge because the receipt printer is not
      // ready. Surface the blocking Out-of-Order overlay (App.tsx reads printerStatus)
      // so the customer/staff sees the real reason, and never charge-without-print.
      if (error instanceof PrinterNotReadyError) {
        setPrinterStatus(error.printerState)
        setCurrentStep('PAYMENT_METHOD')
        return
      }
      toast.error(t('terminalError'))
      setCurrentStep('PAYMENT_METHOD')
    })

    return () => {
      active = false
    }
  }, [orderId, setCurrentStep, setPrinterStatus, startPayment, t])

  const contentTransition = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.2 }
  }

  return (
    <Step>
      <AnimatePresence mode="wait">
        {status === 'PAYMENT' && (
          <PaymentPendingContent key="PAYMENT_TITLE" as={motion.div} {...contentTransition}>
            <h2>{t('cardPaymentPending')}</h2>
          </PaymentPendingContent>
        )}
        {status === 'ORDER' && (
          <PaymentPendingContent as={motion.div} {...contentTransition}>
            <h2>{t('orderFinalization')}</h2>
            <Loader />
          </PaymentPendingContent>
        )}
      </AnimatePresence>
      <PaymentPendingPointer hide={status !== 'PAYMENT'}>
        <Pointer>{terminalMessage || t('terminalCodes.default')}</Pointer>
      </PaymentPendingPointer>
    </Step>
  )
}

export default Payment
